import { useState, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { SlideToConfirm } from '../ui/SlideToConfirm'
import { formatBytes } from '../ui/bytes'
import { copyText } from '../ui/clipboard'
import { BffError, createVault as storeVault, getBackups, resetVault, setVaultPassword, type StoredVault } from '../transport/relay'
import {
  MIN_PASSWORD,
  WrongSecret,
  createVault,
  passwordWrap,
  unlockWithPassword,
  unlockWithRecoveryCode,
} from './vaultCrypto'
import { VAULT_PROMISES } from './backupWords'
import { FaceIdButton, useFaceId } from './FaceIdButton'
import { FaceIdFailed, makeVaultPasskey, unlockWithFaceId } from './passkey'
import { addVaultPasskey } from '../transport/relay'

/**
 * The vault's four doors — set it up, open it, change its password with the recovery
 * code, reset it — shared by the agent card's backup and the Backups list in More.
 *
 * PASSWORD MANAGERS ARE THE FACE ID. Owner, 2026-09-29: 輸入他的密碼, 或者是使用 Apple 的
 * Password, 或使用 Face ID, 或使用 Touch ID. Each door is a real <form> with a username
 * field and `autocomplete` set, which is what lets Apple Passwords (and any password
 * manager) offer a strong password at setup, save it on submit, and fill it behind Face
 * ID or Touch ID afterwards — on iPhone, Mac, and in the browser alike. The username is
 * a fixed label, so the saved entry reads as the backup vault and not as a sign-in.
 */

const VAULT_USERNAME = 'Avengents backup vault'

function UsernameField() {
  return (
    <input
      type="text"
      name="username"
      autoComplete="username"
      value={VAULT_USERNAME}
      readOnly
      tabIndex={-1}
      aria-hidden="true"
      className="bk-username"
    />
  )
}

function VaultPromises({ compact = false }: { compact?: boolean }) {
  return (
    <ul className={`bk-promises${compact ? ' compact' : ''}`}>
      <li>{VAULT_PROMISES.notStored}</li>
      <li>{VAULT_PROMISES.noRecovery}</li>
      {!compact && <li>{VAULT_PROMISES.newVaultDeletes}</li>}
    </ul>
  )
}

const errText = (e: unknown) =>
  e instanceof BffError
    ? String((e.body as { message?: unknown }).message ?? e.message)
    : String(e instanceof Error ? e.message : e)

// ── set up ──────────────────────────────────────────────────────────────────

/**
 * A new vault. The recovery code is shown ONCE, here, and the next step waits until the
 * person says they kept it — there is no second chance to see it.
 */
export function VaultSetup({
  machine,
  onReady,
  onBack,
  onExists,
  continueLabel,
}: {
  machine: string
  /** The vault exists and the person has their code: carry on. */
  onReady: () => void
  onBack: () => void
  /** Another device made a vault meanwhile — open that one instead. */
  onExists: () => void
  continueLabel: string
}) {
  const [pw, setPw] = useState('')
  const [pw2, setPw2] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [code, setCode] = useState<string | null>(null)
  const [kept, setKept] = useState(false)
  const [copied, setCopied] = useState<'no' | 'yes' | null>(null)

  const short = pw.length > 0 && pw.length < MIN_PASSWORD
  const differ = pw2.length > 0 && pw !== pw2
  const ok = pw.length >= MIN_PASSWORD && pw === pw2

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!ok || busy) return
    setBusy(true)
    setErr(null)
    try {
      const { vault, recoveryCode } = await createVault(pw)
      await storeVault(vault as StoredVault)
      setCode(recoveryCode)
    } catch (x) {
      if (x instanceof BffError && x.code === 'vault_exists') {
        onExists()
        return
      }
      setErr(errText(x))
    } finally {
      setBusy(false)
    }
  }

  if (code) {
    return (
      <div className="sm-fork bk-form">
        <p className="bk-title">Your recovery code</p>
        <p className="sm-hint bk-lead">
          This is the only time it is shown. Keep it somewhere safe — your password manager, or on
          paper. With it you can set a new password without losing your backups.
        </p>
        <div className="bk-code" data-testid="recovery-code">
          {code}
        </div>
        {copied === 'no' && (
          <p className="sm-hint bk-bad">Could not copy here — select the code above and copy it yourself.</p>
        )}
        <div className="bk-code-actions">
          <button
            type="button"
            className="sheet-btn"
            onClick={() => {
              void copyText(code).then((r) => setCopied(r.ok ? 'yes' : 'no'))
            }}
          >
            {copied === 'yes' ? 'Copied' : 'Copy'}
          </button>
        </div>
        <label className="bk-check">
          <input type="checkbox" checked={kept} onChange={(e) => setKept(e.target.checked)} />
          <span>I have saved my recovery code</span>
        </label>
        <div className="sm-actions">
          <button type="button" className="sheet-btn primary" disabled={!kept} onClick={onReady}>
            {continueLabel}
          </button>
        </div>
      </div>
    )
  }

  return (
    <form className="sm-fork bk-form" onSubmit={(e) => void submit(e)}>
      <p className="bk-title">Set up your backup vault</p>
      <p className="sm-hint bk-lead">
        Backups are encrypted on {machine} before they leave it. Only your vault password — or
        your recovery code — can open them.
      </p>
      <VaultPromises />
      <UsernameField />
      <label className="sm-field">
        <span className="sm-label">Vault password</span>
        <input
          className="sm-input"
          type="password"
          name="new-password"
          autoComplete="new-password"
          minLength={MIN_PASSWORD}
          value={pw}
          disabled={busy}
          onChange={(e) => setPw(e.target.value)}
        />
      </label>
      <label className="sm-field">
        <span className="sm-label">Type it again</span>
        <input
          className="sm-input"
          type="password"
          name="confirm-password"
          autoComplete="new-password"
          value={pw2}
          disabled={busy}
          onChange={(e) => setPw2(e.target.value)}
        />
      </label>
      {(short || differ) && (
        <p className="sm-hint bk-bad">
          {short ? `At least ${MIN_PASSWORD} characters.` : 'The two passwords are different.'}
        </p>
      )}
      {err && (
        <p className="err-line" role="alert">
          {err}
        </p>
      )}
      <div className="sm-actions">
        <button type="button" className="sheet-btn" disabled={busy} onClick={onBack}>
          Back
        </button>
        <button type="submit" className="sheet-btn primary" disabled={!ok || busy}>
          {busy ? 'Creating…' : 'Create vault'}
        </button>
      </div>
    </form>
  )
}

// ── open ────────────────────────────────────────────────────────────────────

/**
 * The password, checked HERE against the vault — nothing is sent. A backup needs no
 * secret (the machine encrypts to the vault's public key); asking anyway is the owner's
 * design: the person meets their password at every backup, so a forgotten one is found
 * out now, while the recovery code can still fix it, not at the day of a restore.
 */
export function VaultUnlock({
  vault,
  intro,
  submitLabel,
  busyLabel,
  onUnlocked,
  onBack,
  onForgot,
  busy: outerBusy = false,
}: {
  vault: StoredVault
  intro: string
  submitLabel: string
  busyLabel: string
  onUnlocked: () => void
  onBack: () => void
  onForgot: () => void
  busy?: boolean
}) {
  const [pw, setPw] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const faceId = useFaceId(vault)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!pw || busy) return
    setBusy(true)
    setErr(null)
    try {
      const vk = await unlockWithPassword(vault, pw)
      vk.fill(0)
      onUnlocked()
    } catch (x) {
      setErr(x instanceof WrongSecret ? 'That password does not open your vault.' : errText(x))
    } finally {
      setBusy(false)
    }
  }

  const viaFaceId = async () => {
    if (busy) return
    setBusy(true)
    setErr(null)
    try {
      const vk = await unlockWithFaceId(vault)
      vk.fill(0)
      onUnlocked()
    } catch (x) {
      setErr(x instanceof FaceIdFailed ? x.message : errText(x))
    } finally {
      setBusy(false)
    }
  }

  const working = busy || outerBusy
  return (
    <form className="sm-fork bk-form" onSubmit={(e) => void submit(e)}>
      <p className="sm-hint bk-lead">{intro}</p>
      <VaultPromises compact />
      <UsernameField />
      <label className="sm-field">
        <span className="sm-label">Vault password</span>
        <input
          className="sm-input"
          type="password"
          name="password"
          autoComplete="current-password"
          value={pw}
          disabled={working}
          onChange={(e) => setPw(e.target.value)}
        />
      </label>
      {err && (
        <p className="err-line" role="alert">
          {err}
        </p>
      )}
      <div className="sm-actions">
        <button type="button" className="bk-link" disabled={working} onClick={onForgot}>
          Forgot your password?
        </button>
        <button type="button" className="sheet-btn" disabled={working} onClick={onBack}>
          Back
        </button>
        {faceId && <FaceIdButton label="Face ID" disabled={working} onClick={() => void viaFaceId()} />}
        <button type="submit" className="sheet-btn primary" disabled={!pw || working}>
          {working ? busyLabel : submitLabel}
        </button>
      </div>
    </form>
  )
}

// ── a new password, with the recovery code ─────────────────────────────────

export function VaultRecover({
  vault,
  onDone,
  onBack,
  onLostBoth,
}: {
  vault: StoredVault
  onDone: () => void
  onBack: () => void
  onLostBoth: () => void
}) {
  const [code, setCode] = useState('')
  const [pw, setPw] = useState('')
  const [pw2, setPw2] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const ok = code.trim().length > 0 && pw.length >= MIN_PASSWORD && pw === pw2

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!ok || busy) return
    setBusy(true)
    setErr(null)
    try {
      const vk = await unlockWithRecoveryCode(vault, code)
      const wrapped = await passwordWrap(vk, pw)
      vk.fill(0)
      await setVaultPassword(wrapped)
      onDone()
    } catch (x) {
      setErr(x instanceof WrongSecret ? 'That recovery code does not open your vault.' : errText(x))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="sm-fork bk-form" onSubmit={(e) => void submit(e)}>
      <p className="bk-title">Set a new password</p>
      <p className="sm-hint bk-lead">
        Your recovery code opens the vault, and your backups are kept. We never store the new
        password either.
      </p>
      <label className="sm-field">
        <span className="sm-label">Recovery code</span>
        <input
          className="sm-input"
          name="recovery-code"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
          value={code}
          disabled={busy}
          onChange={(e) => setCode(e.target.value)}
        />
      </label>
      <UsernameField />
      <label className="sm-field">
        <span className="sm-label">New vault password</span>
        <input
          className="sm-input"
          type="password"
          name="new-password"
          autoComplete="new-password"
          minLength={MIN_PASSWORD}
          value={pw}
          disabled={busy}
          onChange={(e) => setPw(e.target.value)}
        />
      </label>
      <label className="sm-field">
        <span className="sm-label">Type it again</span>
        <input
          className="sm-input"
          type="password"
          name="confirm-password"
          autoComplete="new-password"
          value={pw2}
          disabled={busy}
          onChange={(e) => setPw2(e.target.value)}
        />
      </label>
      {pw.length > 0 && pw.length < MIN_PASSWORD && <p className="sm-hint bk-bad">At least {MIN_PASSWORD} characters.</p>}
      {pw2.length > 0 && pw !== pw2 && <p className="sm-hint bk-bad">The two passwords are different.</p>}
      {err && (
        <p className="err-line" role="alert">
          {err}
        </p>
      )}
      <div className="sm-actions">
        <button type="button" className="bk-link" disabled={busy} onClick={onLostBoth}>
          Lost your recovery code too?
        </button>
        <button type="button" className="sheet-btn" disabled={busy} onClick={onBack}>
          Back
        </button>
        <button type="submit" className="sheet-btn primary" disabled={!ok || busy}>
          {busy ? 'Saving…' : 'Set password'}
        </button>
      </div>
    </form>
  )
}

// ── a new password, knowing the current one ───────────────────────────────

/**
 * CHANGE THE VAULT PASSWORD — owner, 2026-09-30, from the main menu's Account card. The
 * backups are locked with the vault KEY, and the password only locks that key: with the
 * current password the same key is re-locked under the new one, so every backup stays
 * readable. Losing the old password is the recovery code's door, and losing both is reset's.
 */
export function VaultChangePassword({ vault, onDone, onBack }: { vault: StoredVault; onDone: () => void; onBack: () => void }) {
  const [cur, setCur] = useState('')
  const [pw, setPw] = useState('')
  const [pw2, setPw2] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const ok = !!cur && pw.length >= MIN_PASSWORD && pw === pw2 && pw !== cur

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!ok || busy) return
    setBusy(true)
    setErr(null)
    try {
      const vk = await unlockWithPassword(vault, cur)
      const wrapped = await passwordWrap(vk, pw)
      vk.fill(0)
      await setVaultPassword(wrapped)
      onDone()
    } catch (x) {
      setErr(x instanceof WrongSecret ? 'That is not your current vault password.' : errText(x))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="sm-fork bk-form" onSubmit={(e) => void submit(e)}>
      <p className="sm-hint bk-lead">Your backups are kept — they open with the new password.</p>
      <UsernameField />
      <label className="sm-field">
        <span className="sm-label">Current password</span>
        <input className="sm-input" type="password" name="password" autoComplete="current-password"
          value={cur} disabled={busy} onChange={(e) => setCur(e.target.value)} />
      </label>
      <label className="sm-field">
        <span className="sm-label">New password</span>
        <input className="sm-input" type="password" name="new-password" autoComplete="new-password"
          minLength={MIN_PASSWORD} value={pw} disabled={busy} onChange={(e) => setPw(e.target.value)} />
      </label>
      <label className="sm-field">
        <span className="sm-label">Type it again</span>
        <input className="sm-input" type="password" name="confirm-password" autoComplete="new-password"
          value={pw2} disabled={busy} onChange={(e) => setPw2(e.target.value)} />
      </label>
      {pw.length > 0 && pw.length < MIN_PASSWORD && <p className="sm-hint bk-bad">At least {MIN_PASSWORD} characters.</p>}
      {pw2.length > 0 && pw !== pw2 && <p className="sm-hint bk-bad">The two passwords are different.</p>}
      {err && (
        <p className="err-line" role="alert">
          {err}
        </p>
      )}
      <div className="sm-actions">
        <button type="button" className="sheet-btn" disabled={busy} onClick={onBack}>
          Back
        </button>
        <button type="submit" className="sheet-btn primary" disabled={!ok || busy}>
          {busy ? 'Saving…' : 'Change password'}
        </button>
      </div>
    </form>
  )
}

// ── reset ───────────────────────────────────────────────────────────────────

/**
 * Delete every backup, then the vault — the owner's rule: 在設置新的 password 的時候,
 * 需要將他之前所有的 backup 的東西都清除. Behind a slide, like ending an agent: a stray
 * tap cannot do it. The count is shown before, because "everything" should have a size.
 */
export function VaultReset({ onDone, onBack }: { onDone: (deleted: number) => void; onBack: () => void }) {
  const list = useQuery({ queryKey: ['backups'], queryFn: getBackups, staleTime: 0 })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const rows = (list.data?.backups ?? []).filter((b) => b.state === 'ready')
  const bytes = list.data?.used_bytes ?? 0

  return (
    <div className="sm-fork bk-form">
      <p className="bk-title bk-danger-title">Reset your backup vault?</p>
      <p className="sm-hint bk-lead">
        {list.isLoading
          ? 'Counting your backups…'
          : rows.length === 0
            ? 'You have no backups, so nothing will be lost.'
            : `This permanently deletes all ${rows.length} backup${rows.length === 1 ? '' : 's'} (${formatBytes(bytes)}). Without the old password or recovery code they can’t be opened, so they are removed rather than kept.`}{' '}
        Then you set up a new vault with a new password.
      </p>
      {err && (
        <p className="err-line" role="alert">
          {err}
        </p>
      )}
      <div className="bk-slide">
      <SlideToConfirm
        label={rows.length ? 'Slide to delete all backups' : 'Slide to reset the vault'}
        busyLabel="Deleting…"
        busy={busy}
        onConfirm={() => {
          setBusy(true)
          setErr(null)
          resetVault().then(
            (r) => {
              setBusy(false)
              onDone(r.deleted ?? 0)
            },
            (x: unknown) => {
              setBusy(false)
              setErr(errText(x))
            },
          )
        }}
      />
      </div>
      <div className="sm-actions">
        <button type="button" className="sheet-btn" disabled={busy} onClick={onBack}>
          Keep my backups
        </button>
      </div>
    </div>
  )
}

// ── Face ID / Touch ID ──────────────────────────────────────────────────────

/**
 * TURN ON FACE ID — owner, 2026-10-01. The password opens the vault key once; a passkey is
 * made (Face ID / Touch ID asks), and the key is wrapped under it. The passkey syncs through
 * iCloud Keychain, so the other Apple devices open the vault with Face ID too.
 */
export function VaultFaceIdSetup({ vault, onDone, onBack }: { vault: StoredVault; onDone: () => void; onBack: () => void }) {
  const [pw, setPw] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!pw || busy) return
    setBusy(true)
    setErr(null)
    try {
      const vk = await unlockWithPassword(vault, pw)
      try {
        await addVaultPasskey(await makeVaultPasskey(vault, vk))
      } finally {
        vk.fill(0)
      }
      onDone()
    } catch (x) {
      setErr(x instanceof WrongSecret ? 'That password does not open your vault.' : x instanceof FaceIdFailed ? x.message : errText(x))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="sm-fork bk-form" onSubmit={(e) => void submit(e)}>
      <p className="sm-hint bk-lead">Enter your vault password once. After that, Face ID or Touch ID opens your vault.</p>
      <UsernameField />
      <label className="sm-field">
        <span className="sm-label">Vault password</span>
        <input
          className="sm-input"
          type="password"
          name="password"
          autoComplete="current-password"
          value={pw}
          disabled={busy}
          onChange={(e) => setPw(e.target.value)}
        />
      </label>
      {err && (
        <p className="err-line" role="alert">
          {err}
        </p>
      )}
      <div className="sm-actions">
        <button type="button" className="sheet-btn" disabled={busy} onClick={onBack}>
          Back
        </button>
        <button type="submit" className="sheet-btn primary" disabled={!pw || busy}>
          {busy ? 'Setting up…' : 'Turn on'}
        </button>
      </div>
    </form>
  )
}
